import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { makeStyles } from '@material-ui/core/styles';
import colors from '../../../foundations/colors.module.scss';
import typography from '../../../foundations/typography.module.scss';
import { menuStyles } from '../../Menu/Menu.styles';
const baseFont = {
    fontFamily: typography.fontFamilyLight,
    color: colors.dark700,
    fontSize: typography.fontSizeP1
};
const signatureFont = {
    fontFamily: "'Satisfy-Regular', cursive",
    fontWeight: 400,
    lineHeight: 1
};
// WHY: an <input> clips at its content box and Satisfy's j starts 216/1024 em left of the origin; em keeps it size-proof
const signatureGlyphOverhang = '0.22em';
const signatureTextStart = 16;
const signatureSizes = {
    medium: {
        height: 80,
        fontSize: 56
    },
    small: {
        height: 50,
        fontSize: 38
    }
};
const defaultBorderColor = colors.dark900;
const hoverColor = colors.dark900;
const focusedColor = colors.purple800;
const subtleBorderColor = colors.dark500;
const subtleBorderEmphasisColor = colors.dark900;
const errorColor = colors.red950;
const warningColor = colors.orange900;
const successColor = colors.green900;
const disabledColor = {
    text: colors.dark500,
    border: colors.dark300
};
const placeholderColor = colors.dark400;
export const useBaseTextFieldStyles = makeStyles({
    root: {
        width: (props)=>props.width || (props.helperTextReserveSpace ? 'min-content' : '100%'),
        minWidth: (props)=>props.minWidth || 0,
        '& .MuiSelect-select': {
            backgroundColor: 'transparent'
        }
    },
    inputRoot: (param)=>{
        let { disableSuffixPadding, helperTextReserveSpace, width, isSignature, size = 'medium', textColor } = param;
        return _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, baseFont), {
            // WHY: MUI puts function rules in a sheet injected after the static one, so signature values must live here to win
            backgroundColor: isSignature ? 'transparent' : colors.dark50
        }), helperTextReserveSpace ? {
            width: width || 'fit-content'
        } : {}), {
            // Disable input type=number arrows in Chrome, Safari, Edge, Opera
            '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
            },
            // Disable input type=number arrows in Firefox
            '& input[type=number]': {
                '-moz-appearance': 'textfield'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: defaultBorderColor,
                borderRadius: 4,
                '& span': {
                    fontFamily: typography.fontFamilyLight
                }
            },
            '& .MuiOutlinedInput-inputAdornedStart': {
                paddingLeft: 8
            },
            '& .MuiOutlinedInput-inputAdornedEnd': {
                paddingRight: 8
            },
            '&.MuiOutlinedInput-adornedEnd': {
                paddingRight: disableSuffixPadding ? 0 : 14
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hoverColor,
                borderWidth: 2
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: focusedColor
                },
                '& .MuiOutlinedInput-input': {
                    caretColor: focusedColor
                }
            },
            '&.Mui-disabled': {
                color: disabledColor.text,
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: disabledColor.border
                },
                '& .MuiOutlinedInput-input': {
                    color: isSignature && textColor ? textColor : disabledColor.text,
                    '&::placeholder': _object_spread_props(_object_spread({}, isSignature ? _object_spread_props(_object_spread({}, signatureFont), {
                        fontSize: signatureSizes[size].fontSize
                    }) : baseFont), {
                        opacity: 0.5
                    })
                }
            },
            '&.Mui-error.MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: errorColor
                },
                '& .MuiOutlinedInput-input': {
                    caretColor: errorColor
                }
            },
            '&.MuiOutlinedInput-multiline': {
                minHeight: '46px',
                padding: '8px 0'
            }
        });
    },
    inputInput: (param)=>{
        let { isSignature, textColor, size = 'medium' } = param;
        return _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, baseFont, textColor ? {
            color: textColor
        } : {}), {
            padding: '4px 16px',
            lineHeight: '24px'
        }), isSignature ? _object_spread_props(_object_spread({}, signatureFont, signatureSizes[size]), {
            textIndent: signatureGlyphOverhang,
            paddingLeft: "calc(".concat(signatureTextStart, "px - ").concat(signatureGlyphOverhang, ")")
        }) : {}), {
            '&::placeholder': _object_spread({}, isSignature ? _object_spread_props(_object_spread({}, signatureFont), {
                fontSize: signatureSizes[size].fontSize,
                color: "var(--text-secondary, ".concat(colors.dark500, ")"),
                opacity: 1
            }) : _object_spread_props(_object_spread({}, baseFont), {
                opacity: 0.5
            })),
            '&:-webkit-autofill': _object_spread({}, isSignature ? _object_spread_props(_object_spread({}, signatureFont), {
                fontSize: signatureSizes[size].fontSize
            }) : baseFont, textColor ? {
                color: textColor
            } : {}),
            '&:autofill': _object_spread({}, isSignature ? _object_spread_props(_object_spread({}, signatureFont), {
                fontSize: signatureSizes[size].fontSize
            }) : baseFont, textColor ? {
                color: textColor
            } : {})
        });
    },
    inputInput_medium: {
        height: 38,
        '&.MuiSelect-select': {
            lineHeight: '38px'
        }
    },
    inputInput_small: {
        height: 32,
        '&.MuiSelect-select': {
            lineHeight: '32px'
        }
    },
    inputInputCompact: {
        height: 24,
        '&.MuiSelect-select': {
            lineHeight: '24px'
        }
    },
    inputRootSubtleBorder: {
        '&& .MuiOutlinedInput-notchedOutline': {
            borderColor: subtleBorderColor,
            borderWidth: 1
        },
        '&&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: subtleBorderEmphasisColor,
            borderWidth: 1
        },
        '&&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: subtleBorderEmphasisColor,
            borderWidth: 1
        },
        '&&.Mui-focused .MuiOutlinedInput-input': {
            caretColor: subtleBorderEmphasisColor
        }
    },
    label: _object_spread_props(_object_spread({}, baseFont), {
        '&.Mui-disabled': {
            color: disabledColor.text
        },
        '&.Mui-focused': {
            color: focusedColor
        },
        '&.Mui-error': {
            color: baseFont.color,
            '&.MuiInputLabel-shrink': {
                color: errorColor
            },
            '& .sg-tf-asterix': {
                color: errorColor
            }
        }
    }),
    label_medium: {
        transform: 'translate(16px, 16px) scale(1)'
    },
    label_small: {
        transform: 'translate(16px, 13px) scale(1)'
    },
    ellipsis: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    helperText: _object_spread_props(_object_spread({}, baseFont), {
        fontSize: 12,
        marginTop: 0,
        marginLeft: 0,
        '&.positionFixed': {
            position: 'absolute',
            bottom: 0,
            left: 0,
            transform: 'translateY(100%)'
        },
        lineHeight: 'normal',
        '&.Mui-disabled': {
            color: disabledColor.text
        },
        '&.Mui-error': {
            color: errorColor
        }
    }),
    helperTextCollapse: {
        padding: '0 4px',
        marginTop: '4px !important',
        marginLeft: '0 !important',
        marginRight: '0 !important',
        marginBottom: '0 !important',
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: '100%'
    },
    collapseWrapper: {
        width: '100%',
        marginTop: 0,
        '& > div': {
            width: '100%',
            marginTop: '0 !important'
        }
    },
    success: {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: "".concat(successColor, " !important")
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            color: successColor
        },
        '& .sg-tf-asterix': {
            color: successColor
        }
    },
    warning: {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: "".concat(warningColor, " !important")
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            color: warningColor
        },
        '& .sg-tf-asterix': {
            color: warningColor
        }
    },
    menu: _object_spread({
        maxHeight: (props)=>props.menuMaxHeight
    }, menuStyles),
    noOutline: {
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
        }
    },
    noWrapSuffix: {
        whiteSpace: 'nowrap'
    },
    selectPlaceholder: {
        color: placeholderColor
    },
    wrapper: (props)=>({
            display: 'flex',
            flexDirection: 'column',
            width: props.width || (props.helperTextReserveSpace ? 'min-content' : '100%'),
            minWidth: props.minWidth || 0
        })
});
